import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import SettingsGear from '../components/SettingsGear';
import { LogOut, Book, Pencil, Award, Trophy, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API_URL = window.location.hostname.includes('preview.emergentagent.com')
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

export default function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const { currentColors } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [c1, c2, c3] = currentColors;

  useEffect(() => {
    axios.get(`${API_URL}/api/leaderboard`)
      .then(res => setLeaderboard(res.data.leaderboard))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const podiumColors = ['#FFD700', '#C0C0C0', '#CD7F32'];
  const podiumLabels = ['1st', '2nd', '3rd'];

  return (
    <div className="min-h-screen bg-[var(--page-bg)] pb-20" data-testid="leaderboard-page">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b-[1.5px] border-[var(--border-soft)] px-4 sm:px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/dashboard')} className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c1)] transition-colors" data-testid="nav-diary">
              <Book className="w-4 h-4 inline mr-1" /> {t('diary')}
            </button>
            <button onClick={() => navigate('/drawings')} className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c3)] transition-colors" data-testid="nav-drawings">
              <Pencil className="w-4 h-4 inline mr-1" /> {t('drawings')}
            </button>
            <button onClick={() => navigate('/stickers')} className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c2)] transition-colors" data-testid="nav-stickers">
              <Award className="w-4 h-4 inline mr-1" /> {t('awards')}
            </button>
            <button className="px-5 py-2 rounded-full bg-[var(--c2)] border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A]" data-testid="nav-leaderboard">
              <Trophy className="w-4 h-4 inline mr-1" /> Leaderboard
            </button>
          </div>
          <div className="flex items-center gap-3">
            {user?.avatar && (
              <img src={`/avatars/${user.avatar}.png`} alt="avatar" className="w-8 h-8 rounded-full border-[1.5px] border-[var(--border-soft)] object-cover" data-testid="navbar-avatar" />
            )}
            <span className="text-[#1A1A1A] font-semibold hidden sm:inline">{t('hi')}, {user?.name}!</span>
            <button onClick={handleLogout} className="px-4 py-2 rounded-full border-[1.5px] border-[var(--border-soft)] shadow-sm bg-white hover:bg-[var(--c1)] text-[#1A1A1A] font-heading font-bold text-base transition-all" data-testid="logout-button">
              <LogOut className="w-4 h-4 inline mr-1" /> {t('logout')}
            </button>
            <SettingsGear />
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-[#1A1A1A] mb-2">Leaderboard</h1>
          <p className="text-[#6B7280] font-medium">See who's been the most active!</p>
        </div>

        {/* Points guide */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {[
            { label: 'Diary Entry', pts: 10, color: c1 },
            { label: 'Drawing', pts: 10, color: c3 },
            { label: 'Award', pts: 20, color: c2 },
            { label: 'WYR Question', pts: 15, color: c1 },
          ].map(item => (
            <span key={item.label} className="px-3 py-1.5 rounded-full text-xs font-semibold border-[1.5px] border-[var(--border-soft)]" style={{ backgroundColor: item.color + '40' }}>
              {item.label}: +{item.pts}
            </span>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-3 border-[var(--c1)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : leaderboard.length === 0 ? (
          <p className="text-center text-[#6B7280] py-20">No users yet!</p>
        ) : (
          <div className="space-y-3">
            {/* Podium — top 3 */}
            {leaderboard.length >= 1 && (
              <div className="flex justify-center items-end gap-4 mb-10 pt-4">
                {[1, 0, 2].map(idx => {
                  const entry = leaderboard[idx];
                  if (!entry) return <div key={idx} className="w-24" />;
                  const isFirst = idx === 0;
                  const isMe = entry.name === user?.name;
                  return (
                    <div key={idx} className="flex flex-col items-center" style={{ marginTop: isFirst ? 0 : 24 }}>
                      <div className={`relative rounded-full border-[3px] flex items-center justify-center overflow-hidden shadow-md mb-2 ${isMe ? 'ring-2 ring-offset-2 ring-[var(--c1)]' : ''}`}
                        style={{ borderColor: podiumColors[idx], backgroundColor: podiumColors[idx] + '30', width: isFirst ? 80 : 64, height: isFirst ? 80 : 64 }}
                        data-testid={`podium-${idx + 1}`}
                      >
                        {entry.avatar ? (
                          <img src={`/avatars/${entry.avatar}.png`} alt={entry.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[#1A1A1A] font-heading" style={{ fontSize: isFirst ? 28 : 22 }}>
                            {entry.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span className="absolute -bottom-1 -right-1 text-xs font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: podiumColors[idx] }}>
                          {podiumLabels[idx]}
                        </span>
                      </div>
                      <p className={`font-bold text-sm text-[#1A1A1A] ${isMe ? 'underline decoration-[var(--c1)] decoration-2' : ''}`}>{entry.name}</p>
                      <p className="text-lg font-heading font-bold" style={{ color: podiumColors[idx] }}>{entry.points}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Full list */}
            {leaderboard.map((entry, i) => {
              const isMe = entry.name === user?.name;
              return (
                <div
                  key={i}
                  className={`flex items-center gap-4 px-5 py-4 rounded-2xl border-[1.5px] transition-all ${
                    isMe
                      ? 'bg-[var(--c1)] border-[var(--c1-accent)] shadow-md'
                      : 'bg-white border-[var(--border-soft)] hover:shadow-sm'
                  }`}
                  data-testid={`leaderboard-row-${i}`}
                >
                  {/* Rank */}
                  <div className="w-8 text-center">
                    {i < 3 ? (
                      <span className="text-lg font-bold" style={{ color: podiumColors[i] }}>
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}
                      </span>
                    ) : (
                      <span className="text-sm font-bold text-[#9CA3AF]">#{i + 1}</span>
                    )}
                  </div>

                  {/* Avatar */}
                  {entry.avatar ? (
                    <img src={`/avatars/${entry.avatar}.png`} alt={entry.name} className="w-10 h-10 rounded-full border-[1.5px] border-[var(--border-soft)] object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold text-lg text-[#1A1A1A] border-[1.5px] border-[var(--border-soft)]"
                      style={{ backgroundColor: isMe ? 'var(--c1-accent)' : 'var(--c3-light)' }}>
                      {entry.name.charAt(0).toUpperCase()}
                    </div>
                  )}

                  {/* Name + breakdown */}
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-[#1A1A1A] truncate ${isMe ? 'flex items-center gap-1' : ''}`}>
                      {entry.name} {isMe && <span className="text-xs font-medium text-[#6B7280]">(you)</span>}
                    </p>
                    <div className="flex gap-3 text-xs text-[#9CA3AF] mt-0.5">
                      {entry.diary_count > 0 && <span>{entry.diary_count} entries</span>}
                      {entry.drawing_count > 0 && <span>{entry.drawing_count} drawings</span>}
                      {entry.award_count > 0 && <span>{entry.award_count} awards</span>}
                      {entry.wyr_count > 0 && <span>{entry.wyr_count} questions</span>}
                    </div>
                  </div>

                  {/* Points */}
                  <div className="text-right">
                    <p className="text-xl font-heading font-bold text-[#1A1A1A]">{entry.points}</p>
                    <p className="text-xs text-[#9CA3AF]">pts</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
